import SockJS from 'sockjs-client';
import { ref } from 'vue';

export interface WebSocketMessage {
  messageType: 'LOGOUT' | 'AI_REPORT_DONE' | string;
  currentTabId?: string;
  message?: any;
  [key: string]: any;
}

export type MessageHandler = (message: WebSocketMessage) => void;

// 현재 탭의 고유 ID
const currentTabId = Math.random().toString(36).substr(2, 9);

// 싱글톤 인스턴스 (여러 컴포넌트에서 공유)
let socket: WebSocket | null = null;
let broadcastChannel: BroadcastChannel | null = null;
const isConnected = ref(false);
const messageHandlers = new Set<MessageHandler>();

export function useWebSocket() {
  /**
   * WebSocket 서버 URL 생성
   * 개발환경: Vite 프록시를 통해 /ws로 연결
   * 프로덕션: 동일 도메인의 /ws
   */
  const getWebSocketUrl = (): string => {
    const protocol = window.location.protocol === 'https:' ? 'https:' : 'http:';
    return `${protocol}//${window.location.host}/ws`;
  };

  /**
   * WebSocket 연결 초기화
   */
  const connect = (): void => {
    // 이미 연결되어 있으면 무시
    if (socket) {
      console.log('[WebSocket] 이미 연결되어 있습니다.');
      return;
    }

    // iframe 내부에서는 연결하지 않음 (부모 창에서만 연결)
    if (window.self !== window.top) {
      console.log('[WebSocket] iframe 내부에서는 연결하지 않습니다.');
      return;
    }

    try {
      const wsUrl = getWebSocketUrl();
      console.log('[WebSocket] 연결 시도:', wsUrl);

      socket = new SockJS(wsUrl) as unknown as WebSocket;

      socket.onopen = () => {
        console.log('[WebSocket] 연결 성공');
        isConnected.value = true;
      };

      socket.onmessage = (event: MessageEvent) => {
        try {
          const msgObj: WebSocketMessage = JSON.parse(event.data);

          // 자신이 보낸 LOGOUT 메시지는 무시
          if (msgObj.messageType === 'LOGOUT' && msgObj.currentTabId === currentTabId) {
            return;
          }

          handleIncomingMessage(msgObj);
        } catch (error) {
          console.error('[WebSocket] 메시지 파싱 오류:', error);
        }
      };

      socket.onerror = (error: Event) => {
        console.error('[WebSocket] 연결 오류:', error);
        isConnected.value = false;
      };

      socket.onclose = () => {
        console.log('[WebSocket] 연결 종료');
        isConnected.value = false;
        socket = null;
      };

      // BroadcastChannel 초기화 (탭 간 메시지 공유)
      if (!broadcastChannel) {
        broadcastChannel = new BroadcastChannel('websocket-broadcast');
        broadcastChannel.onmessage = (event: MessageEvent) => {
          handleIncomingMessage(event.data);
        };
      }
    } catch (error) {
      console.error('[WebSocket] 초기화 오류:', error);
    }
  };

  /**
   * 수신 메시지 처리
   */
  const handleIncomingMessage = (msgObj: WebSocketMessage): void => {
    // 등록된 핸들러에게 메시지 전달
    messageHandlers.forEach(handler => {
      try {
        handler(msgObj);
      } catch (error) {
        console.error('[WebSocket] 핸들러 실행 오류:', error);
      }
    });
  };

  /**
   * 메시지 핸들러 등록
   */
  const onMessage = (handler: MessageHandler): void => {
    messageHandlers.add(handler);
  };

  /**
   * 메시지 핸들러 제거
   */
  const offMessage = (handler: MessageHandler): void => {
    messageHandlers.delete(handler);
  };

  /**
   * 다른 탭에 메시지 브로드캐스트
   */
  const broadcastMessage = (message: WebSocketMessage): void => {
    if (broadcastChannel) {
      broadcastChannel.postMessage(message);
    }
  };

  /**
   * WebSocket으로 메시지 전송
   */
  const sendMessage = (data: Record<string, any>): void => {
    const messageWithTabId = { ...data, currentTabId };

    if (socket && socket.readyState === WebSocket.OPEN) {
      socket.send(JSON.stringify(messageWithTabId));
    } else {
      console.warn('[WebSocket] 연결되지 않아 메시지를 보낼 수 없습니다.');
    }
  };

  /**
   * WebSocket 연결 종료
   */
  const disconnect = (): void => {
    if (socket) {
      socket.close();
      socket = null;
    }
    if (broadcastChannel) {
      broadcastChannel.close();
      broadcastChannel = null;
    }
    isConnected.value = false;
    messageHandlers.clear();
  };

  /**
   * 연결 상태 확인
   */
  const getConnectionState = (): boolean => {
    return isConnected.value;
  };

  return {
    connect,
    disconnect,
    sendMessage,
    broadcastMessage,
    onMessage,
    offMessage,
    isConnected,
    currentTabId,
    getConnectionState,
  };
}
